import {Choice} from 'platform/components';
import {VStack} from 'platform/foundation';

import {useState} from 'react';

import {defaultTo, pipe, trim} from 'ramda';

import {useGetTenantQuery} from '@dms/api/core';
import {useGetUsersQuery} from '@dms/api/customer';
import {PhoneNumber, UserStateFilterSchema} from '@dms/api/shared';
import i18n from '@dms/i18n';
import {
  DeletedUserAlert,
  getNaturalPersonFullName,
  getUserStatusIndicator,
  useUserAbsenceIndicators,
  useUsersWithPersistedAssignee,
} from '@dms/shared';

import {RequiredTestIdProps, suffixTestId} from 'shared/web';

export type SignatoryUserSelectType = {
  name: string;
  email: string;
  id: string;
  phoneNumber: PhoneNumber;
};

interface UserSelectChoiceProps extends RequiredTestIdProps {
  onSelect: (userValues: SignatoryUserSelectType) => void;
  defaultUserId?: string;
}

export function UserSelectChoice(props: UserSelectChoiceProps) {
  const [selectedUserId, setSelectedUserId] = useState<string | null>(props.defaultUserId ?? null);

  const {data: users, isLoading} = useGetUsersQuery({
    stateFilter: UserStateFilterSchema.enum.active,
  });

  // Keep a saved signatory who is now blocked/deleted visible in the active-only list,
  // so the selection keeps rendering instead of emitting an empty user on change.
  const usersWithSignatory = useUsersWithPersistedAssignee(users, props.defaultUserId);

  const {data: tenant} = useGetTenantQuery();
  const {getStatusIndicator} = useUserAbsenceIndicators();
  const userOptions = usersWithSignatory.map((user) => ({
    value: user.id,
    label: getNaturalPersonFullName(user),
    statusIndicator: getUserStatusIndicator(user, getStatusIndicator(user.id)),
  }));

  const handleOnChange = (userId: string | null) => {
    setSelectedUserId(userId);

    const selectedUser = usersWithSignatory.find((user) => user.id === userId);
    const phoneNumber = selectedUser?.phoneNumber;
    const tenantFallbackPhoneNumber =
      selectedUser?.phoneNumber ?? tenant?.publicContacts?.phoneNumber;

    const userValues: SignatoryUserSelectType = {
      name: getNaturalPersonFullName(selectedUser) ?? '',
      email: getFormPhoneValue(selectedUser?.email),
      id: selectedUser?.id ?? '',
      phoneNumber: {
        number: getFormPhoneValue(phoneNumber?.number),
        prefix: getFormPhoneValue(tenantFallbackPhoneNumber?.prefix),
        countryCode: getFormPhoneValue(tenantFallbackPhoneNumber?.countryCode),
      },
    };

    props.onSelect(userValues);
  };

  return (
    <VStack spacing={1}>
      <Choice
        isLoading={isLoading}
        value={selectedUserId}
        onChange={handleOnChange}
        options={userOptions}
        isNotClearable
        menuInPortal
        data-testid={suffixTestId('user-select', props)}
        label={i18n.t('entity.document.actions.searchForUser')}
        placeholder={i18n.t('general.labels.searchPlaceholder')}
      />
      <DeletedUserAlert
        users={usersWithSignatory}
        selectedUserId={selectedUserId}
        data-testid={suffixTestId('user-select', props)}
      />
    </VStack>
  );
}

const getFormPhoneValue = pipe(defaultTo<string | undefined>(''), trim);
